import React from 'react';
import { Coffee, Mail, Phone, Download, Trash2 } from 'lucide-react';
import { deleteCoffeePreorder } from '../../services/api';

const PreordersPanel = ({ preorders, setPreorders, showNotification }) => {
  const exportCSV = () => {
    const headers = ['Email', 'Phone', 'Source', 'Product', 'Date'];
    const rows = preorders.map(p => [
      p.email,
      p.phone || '',
      p.source === 'early_bird_15' ? '15% Early Bird' : 'General',
      p.product_id || '',
      new Date(p.created_at).toLocaleDateString()
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coffee-preorders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Exported to CSV!');
  };

  const handleDelete = async (preorder) => {
    if (!window.confirm('Delete this pre-order signup?')) return;
    try {
      await deleteCoffeePreorder(preorder.id);
      setPreorders(prev => prev.filter(p => p.id !== preorder.id));
      showNotification('Pre-order deleted');
    } catch (error) {
      showNotification('Failed to delete', 'error');
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-800">Coffee Pre-orders</h2>
          <p className="text-slate-500 text-sm mt-1">
            {preorders.length} {preorders.length === 1 ? 'person has' : 'people have'} signed up
            {preorders.filter(p => p.source === 'early_bird_15').length > 0 && (
              <span className="ml-2 inline-block bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                {preorders.filter(p => p.source === 'early_bird_15').length} for 15% OFF
              </span>
            )}
          </p>
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
          data-testid="export-preorders-btn"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {preorders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Coffee className="w-16 h-16 mx-auto text-amber-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No pre-orders yet</h3>
          <p className="text-slate-500">Pre-order signups from the Coffee page will appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-sm font-medium text-slate-600">
            <div className="col-span-4">Email</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-3">Source</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1"></div>
          </div>

          <div className="divide-y divide-slate-100">
            {preorders.map((preorder) => (
              <div 
                key={preorder.id} 
                className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 md:px-6 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="md:col-span-4 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <span className="text-slate-800 font-medium truncate">{preorder.email}</span>
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400 flex-shrink-0 md:hidden" />
                  <span className="text-slate-600 text-sm truncate">
                    {preorder.phone || <span className="text-slate-400 italic">&mdash;</span>}
                  </span>
                </div>
                <div className="md:col-span-3 flex items-center gap-2">
                  {preorder.source === 'early_bird_15' ? (
                    <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">
                      15% OFF {preorder.product_id ? `• ${preorder.product_id.replace('-', ' ')}` : ''}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1 rounded-full">
                      Launch Signup
                    </span>
                  )}
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <span className="text-slate-500 text-sm">
                    {new Date(preorder.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <button
                    onClick={() => handleDelete(preorder)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-preorder-${preorder.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreordersPanel;
